import React, { useState } from 'react';
import styled from 'styled-components';

const Panel = styled.div`
  flex: ${props => props.$collapsed ? '0 0 auto' : '1'};
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: ${props => props.$collapsed ? 'visible' : 'hidden'};
`;

const RulesList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const Title = styled.h3`
  margin: 0;
  padding: 16px;
  font-size: 14px;
  font-weight: 600;
  color: #24292f;
  border-bottom: 1px solid #e1e4e8;
  background: #f6f8fa;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: move;
  user-select: none;
  
  &:hover {
    background: #eaeef2;
  }
`;

const CollapseIcon = styled.span`
  margin-left: auto;
  cursor: pointer;
  padding: 4px;
  transition: transform 0.2s;
  transform: ${props => props.$collapsed ? 'rotate(-90deg)' : 'rotate(0deg)'};
  
  &:hover {
    background: rgba(0,0,0,0.1);
    border-radius: 3px;
  }
`;

const RuleItem = styled.div`
  padding: 10px 12px;
  margin: 6px 0;
  border: 1px solid #e1e4e8;
  border-radius: 6px;
  background: white;
  transition: all 0.15s;
  
  &:hover {
    border-color: #0969da;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
`;

const RuleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
`;

const Toggle = styled.input`
  margin-left: 8px;
`;

const AddButton = styled.button`
  width: 100%;
  padding: 10px;
  margin-top: 8px;
  border: 2px dashed #d1d5da;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #0969da;
  transition: all 0.15s;
  
  &:hover {
    background: #f6f8fa;
    border-color: #0969da;
    transform: translateY(-1px);
  }
`;

const RuleForm = styled.div`
  margin-top: 8px;
  padding: 8px;
  border: 1px solid #d1d5da;
  border-radius: 4px;
  background: #f6f8fa;
`;

const Input = styled.input`
  width: 100%;
  padding: 4px;
  margin: 4px 0;
  border: 1px solid #d1d5da;
  border-radius: 2px;
  font-size: 11px;
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 60px;
  padding: 4px;
  margin: 4px 0;
  border: 1px solid #d1d5da;
  border-radius: 2px;
  font-size: 11px;
  resize: vertical;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 4px;
`;

const Button = styled.button`
  flex: 1;
  padding: 4px;
  border: 1px solid #d1d5da;
  border-radius: 2px;
  background: white;
  cursor: pointer;
  font-size: 10px;
  
  &:hover {
    background: #f6f8fa;
  }
`;

function RulesPanel({ rules, onRulesChange, collapsed, onToggle, draggable, onDragStart, onDragOver, onDragEnd }) {
  const [showForm, setShowForm] = useState(false);
  const [newRule, setNewRule] = useState({ name: '', description: '', code: '' });

  const toggleRule = (index) => {
    const updated = rules.map((rule, i) => 
      i === index ? { ...rule, enabled: !rule.enabled } : rule
    );
    onRulesChange(updated);
  };

  const addRule = () => {
    try {
      const validate = new Function('spec', newRule.code);
      const rule = {
        ...newRule,
        enabled: true,
        validate
      };
      onRulesChange([...rules, rule]);
      setNewRule({ name: '', description: '', code: '' });
      setShowForm(false);
    } catch (error) {
      alert('Invalid rule code: ' + error.message);
    }
  };

  const removeRule = (index) => {
    onRulesChange(rules.filter((_, i) => i !== index));
  };

  return (
    <Panel $collapsed={collapsed}>
      <Title
        draggable={draggable}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
      >
        ⚙️ Rules ({rules.length})
        <CollapseIcon $collapsed={collapsed} onClick={(e) => { e.stopPropagation(); onToggle && onToggle(); }}>
          ▼
        </CollapseIcon>
      </Title>
      
      {!collapsed && <RulesList>
        {rules.map((rule, index) => (
        <RuleItem key={index}>
          <RuleHeader>
            {rule.name}
            <div>
              <Toggle
                type="checkbox"
                checked={rule.enabled}
                onChange={() => toggleRule(index)}
              />
              <button onClick={() => removeRule(index)} style={{marginLeft: 4, fontSize: 10}}>×</button>
            </div>
          </RuleHeader>
          <div style={{fontSize: 10, color: '#586069', marginTop: 4}}>
            {rule.description}
          </div>
        </RuleItem>
      ))}

      {showForm ? (
        <RuleForm>
          <Input
            placeholder="Rule name"
            value={newRule.name}
            onChange={(e) => setNewRule({...newRule, name: e.target.value})}
          />
          <Input
            placeholder="Description"
            value={newRule.description}
            onChange={(e) => setNewRule({...newRule, description: e.target.value})}
          />
          <TextArea
            placeholder="return spec.paths && Object.keys(spec.paths).length === 0 ? {message: 'No paths defined', type: 'warning'} : null;"
            value={newRule.code}
            onChange={(e) => setNewRule({...newRule, code: e.target.value})}
          />
          <ButtonGroup>
            <Button onClick={addRule}>Add</Button>
            <Button onClick={() => setShowForm(false)}>Cancel</Button>
          </ButtonGroup>
        </RuleForm>
      ) : (
        <AddButton onClick={() => setShowForm(true)}>
          + Add Rule
        </AddButton>
      )}
      </RulesList>}
    </Panel>
  );
}

export default RulesPanel;